import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Cookie, X } from 'lucide-react';
import { COOKIE_CONSENT_EVENT, COOKIE_CONSENT_KEY, loadAdSense } from '@/lib/ads';

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay for better UX - don't show immediately on page load
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    window.dispatchEvent(new Event(COOKIE_CONSENT_EVENT));
    void loadAdSense();
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    window.dispatchEvent(new Event(COOKIE_CONSENT_EVENT));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background border-t-2 border-border shadow-lg animate-fade-in-up"
      role="dialog"
      aria-label="Cookie consent"
      aria-describedby="cookie-consent-description"
    >
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <Cookie className="w-6 h-6 text-foreground flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p id="cookie-consent-description" className="text-sm text-foreground font-medium">
              We use essential storage for sign-in and settings. With your permission, Google may also
              use cookies or similar identifiers to provide and measure ads. Declining does not block the app.
            </p>
            <p className="text-xs text-foreground">
              Learn more in our{' '}
              <Link to="/privacy-policy" className="text-foreground font-semibold underline underline-offset-2">
                Privacy & Cookie Policy
              </Link>
              .
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDecline}
            className="flex-1 sm:flex-none"
          >
            Decline
          </Button>
          <Button
            size="sm"
            onClick={handleAccept}
            className="flex-1 sm:flex-none gradient-love text-primary-foreground"
          >
            Accept
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDecline}
            className="hidden sm:flex"
            aria-label="Close cookie consent"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
